// ============================================================
// IoTWithCppNode.jsx
//
// Course content page for "IoT with C++ & Node.js" — previously
// a plain, unstyled draft with no route in the dashboard. Per
// Hazzan's direction this is now a full, standalone lesson.
//
// NOTE: this lesson has no entry in IoTDashboard.jsx yet. Add a
// topic (e.g. { title: "IoT with C++ & Node.js", link: "/iot-cpp-node" })
// and the matching <Route> in App.js for it to be reachable.
//
// Fixes applied:
//   1) the original rendered a broken <img> whose src was a blog
//      article URL, not an image file — it would just show a
//      broken-image icon. Replaced with an actual rendered
//      publisher -> broker -> subscriber diagram.
//   2) the image caption used Markdown link syntax
//      ([text](url)) sitting in plain JSX text, which renders as
//      literal brackets and parentheses, not a link.
//   3) the C++ example never called client.connect() anywhere, so
//      client.publish() would silently do nothing — PubSubClient
//      requires an explicit connect() before publish() does
//      anything. Also added the standard "wait for Wi-Fi" loop and
//      a NaN check on the DHT read (a real DHT22 failure mode).
//   4) rebuilt the whole page in the house dark-purple/goldenrod
//      style with arrow-function components and Material Icons,
//      replacing the unstyled plain-HTML draft entirely.
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Paper,
    Stack,
    IconButton,
    Avatar,
} from "@mui/material";

// ── Icons — @mui/icons-material only ──
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import MemoryIcon from "@mui/icons-material/Memory";
import DnsIcon from "@mui/icons-material/Dns";
import TerminalIcon from "@mui/icons-material/Terminal";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ForumIcon from "@mui/icons-material/Forum";
import ThermostatIcon from "@mui/icons-material/Thermostat";

// ── Brand tokens (matches IoTDashboard.jsx / SensorsActuators.jsx) ──
const BRAND = {
    bg: "#100720",
    surface: "rgba(16,7,32,0.85)",
    border: "rgba(255,255,255,0.1)",
    gold: "goldenrod",
    teal: "#2DD4BF",
    white: "#FAFAF9",
    muted: "rgba(255,255,255,0.65)",
    codeBg: "#0b0515",
};

const WHY_CPP = [
    "Direct access to hardware registers and memory",
    "Efficient use of limited resources (RAM, CPU)",
    "Supported by most microcontroller SDKs (Arduino, STM32, ESP32)",
    "Real-time control with precise timing and interrupts",
];

const WHY_NODE = [
    "Handles multiple device connections with ease",
    "Supports MQTT, WebSockets, and REST APIs",
    "Rich ecosystem of packages for cloud, databases, and dashboards",
    "Easy integration with authentication and security layers",
];

const WORKFLOW_STEPS = [
    "Write C++ firmware to read the sensor and format the data",
    "Send data via Wi-Fi/MQTT to a Node.js server",
    "Node.js receives, stores, and visualizes the data",
    "Node.js can publish commands back to the device if needed",
];

// A complete, compiling sketch — the original never called
// client.connect() anywhere, so client.publish() would silently
// do nothing. Also waits for Wi-Fi and checks for a failed read.
const CPP_FIRMWARE = `#include <WiFi.h>
#include <PubSubClient.h>
#include <DHT.h>

const char* ssid = "YourSSID";
const char* password = "YourPassword";
const char* mqttServer = "broker.hivemq.com";

WiFiClient espClient;
PubSubClient client(espClient);
DHT dht(4, DHT22); // GPIO4

void setup() {
  Serial.begin(115200);

  WiFi.begin(ssid, password);
  while (WiFi.status() != WL_CONNECTED) {
    delay(500);
  }

  client.setServer(mqttServer, 1883);
  dht.begin();
}

void loop() {
  if (!client.connected()) {
    client.connect("ESP32_DHT_Node"); // required before publish() will send anything
  }

  float temp = dht.readTemperature();
  if (!isnan(temp)) { // DHT sensors occasionally fail a read
    client.publish("iot/temperature", String(temp).c_str());
  }

  client.loop();
  delay(5000);
}`;

const NODE_SERVER = `const mqtt = require('mqtt');
const client = mqtt.connect('mqtt://broker.hivemq.com');

client.on('connect', () => {
  client.subscribe('iot/temperature');
});

client.on('message', (topic, message) => {
  console.log(\`Temperature: \${message.toString()} °C\`);
});`;

// Sub-components (arrow-function style, defined before first use)

const LanguagePanel = ({ title, icon, color, points }) => (
    <Paper
        elevation={0}
        sx={{
            p: 4,
            background: BRAND.surface,
            border: `1px solid ${color}40`,
            borderRadius: 4,
            height: "100%",
            transition: "0.3s",
            "&:hover": { borderColor: color, transform: "translateY(-5px)" },
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Avatar sx={{ bgcolor: `${color}26`, color, width: 56, height: 56 }}>{icon}</Avatar>
            <Typography variant="h5" sx={{ color: BRAND.white, fontWeight: 800 }}>{title}</Typography>
        </Box>
        <Stack spacing={1.5}>
            {points.map((p) => (
                <Box key={p} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <DoneIcon sx={{ color, fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: BRAND.muted }}>{p}</Typography>
                </Box>
            ))}
        </Stack>
    </Paper>
);

const CodeTerminal = ({ code, label, color }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API isn't available in every browser/context.
        }
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.03)" }}>
                <Typography variant="caption" sx={{ color, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 13, color, lineHeight: 1.5, filter: "brightness(1.3)" }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
};

// Replaces the original's broken <img> with an actual rendered
// diagram of how the firmware and server talk to each other.
const ArchitectureDiagram = () => {
    const nodeStyle = {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        p: 2.5,
        borderRadius: 3,
        background: "rgba(255,255,255,0.03)",
        border: `1px solid ${BRAND.border}`,
        minWidth: 150,
    };

    return (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" justifyContent="center" sx={{ my: 6, flexWrap: "wrap" }}>
            <Box sx={nodeStyle}>
                <ThermostatIcon sx={{ color: BRAND.gold, fontSize: 32 }} />
                <Typography variant="body2" sx={{ color: BRAND.white, fontWeight: 700 }}>ESP32 (C++)</Typography>
                <Typography variant="caption" sx={{ color: BRAND.muted, textAlign: "center" }}>Reads the DHT22 sensor</Typography>
            </Box>
            <ArrowForwardIcon sx={{ color: BRAND.muted, transform: { xs: "rotate(90deg)", sm: "none" } }} />
            <Box sx={nodeStyle}>
                <ForumIcon sx={{ color: BRAND.white, fontSize: 32 }} />
                <Typography variant="body2" sx={{ color: BRAND.white, fontWeight: 700 }}>MQTT Broker</Typography>
                <Typography variant="caption" sx={{ color: BRAND.muted, textAlign: "center" }}>Relays the "iot/temperature" topic</Typography>
            </Box>
            <ArrowForwardIcon sx={{ color: BRAND.muted, transform: { xs: "rotate(90deg)", sm: "none" } }} />
            <Box sx={nodeStyle}>
                <DnsIcon sx={{ color: BRAND.teal, fontSize: 32 }} />
                <Typography variant="body2" sx={{ color: BRAND.white, fontWeight: 700 }}>Node.js Server</Typography>
                <Typography variant="caption" sx={{ color: BRAND.muted, textAlign: "center" }}>Subscribes and logs the reading</Typography>
            </Box>
        </Stack>
    );
};

// ── Main Page ───────────────────────────────────────────────────

const IoTWithCppNode = () => (
    <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
        <Container maxWidth="lg">
            {/* HERO SECTION */}
            <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                <Chip
                    icon={<SchoolIcon style={{ color: BRAND.gold }} />}
                    label="Full-Stack IoT"
                    sx={{ bgcolor: "rgba(218,165,32,0.1)", color: BRAND.gold, border: `1px solid ${BRAND.border}`, mb: 2 }}
                />
                <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                    IoT with <span style={{ color: BRAND.teal }}>C++ & Node.js</span>
                </Typography>
                <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                    C++ gives you speed and direct hardware access for programming microcontrollers. Node.js
                    excels at handling asynchronous data streams and scalable APIs. Together they form a
                    robust stack for real-time, connected IoT applications.
                </Typography>
            </Box>

            {/* LANGUAGE COMPARISON */}
            <Grid container spacing={4} sx={{ mb: 10 }}>
                <Grid item xs={12} md={6}>
                    <LanguagePanel title="Why C++ for IoT?" icon={<MemoryIcon />} color={BRAND.gold} points={WHY_CPP} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <LanguagePanel title="Why Node.js for IoT?" icon={<DnsIcon />} color={BRAND.teal} points={WHY_NODE} />
                </Grid>
            </Grid>

            {/* WORKFLOW */}
            <Box sx={{ mb: 10 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                    <AccountTreeIcon sx={{ fontSize: 32, color: BRAND.gold }} />
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>Workflow: C++ Firmware + Node.js Server</Typography>
                </Box>
                <Stack spacing={1.5}>
                    {WORKFLOW_STEPS.map((step, i) => (
                        <Stack direction="row" spacing={2} key={step}>
                            <Chip label={i + 1} size="small" sx={{ bgcolor: "rgba(218,165,32,0.15)", color: BRAND.gold, fontWeight: 700, minWidth: 28 }} />
                            <Typography variant="body2" sx={{ color: BRAND.muted }}>{step}</Typography>
                        </Stack>
                    ))}
                </Stack>
            </Box>

            <ArchitectureDiagram />

            {/* CODE EXAMPLES */}
            <Grid container spacing={6} sx={{ mb: 10 }}>
                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ color: BRAND.gold, fontWeight: 700, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                        <ThermostatIcon fontSize="small" /> Temperature Sensor on ESP32 (C++)
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Reads a DHT22 sensor and publishes the reading over MQTT.
                    </Typography>
                    <CodeTerminal color={BRAND.gold} label="esp32_dht_publisher.ino" code={CPP_FIRMWARE} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ color: BRAND.teal, fontWeight: 700, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                        <DnsIcon fontSize="small" /> Node.js Server to Receive Data
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Subscribes to the topic and logs every reading as it arrives.
                    </Typography>
                    <CodeTerminal color={BRAND.teal} label="server_sub.js" code={NODE_SERVER} />
                </Grid>
            </Grid>

            {/* FOOTER CTA */}
            <Box sx={{ p: 8, borderRadius: 8, textAlign: "center", background: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}` }}>
                <TerminalIcon sx={{ fontSize: 40, color: BRAND.gold, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Ready to Wire Firmware to the Cloud?</Typography>
                <Typography sx={{ color: BRAND.muted, mb: 4 }}>Flash your ESP32 and watch the readings land on your server.</Typography>
                <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
                    <Chip label="Start C++ Firmware Lab" onClick={() => {}} sx={{ bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 800, px: 3 }} />
                    <Chip label="Launch Node.js Server" onClick={() => {}} sx={{ bgcolor: BRAND.teal, color: BRAND.bg, fontWeight: 800, px: 3 }} />
                </Stack>
            </Box>
        </Container>
    </Box>
);

export default IoTWithCppNode;
